<script setup>
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  pointList: {
    type: Array,
    default: () => []
  }
})
const route = useRoute()
const csvData = ref(null)
const waterLevelData = ref(null)
const currentIndex = useState('currentIndex', () => 0)
const currentPointId = computed(() => props.pointList[currentIndex.value]?.id)
const mapContainer = ref(null)
const selectedPref = ref('none')
const selectedCitycode = ref('none')
const cityList = ref([{ citycode: 'none', cityname: '市区町村' }])
const linkRefs = ref([])
const { sourceGeojson, clusterGeojson, zoomLevel } = usePointCluster()
const { initMap, getRiverRiskData, setRiverRisk, updatePointIcon, setHighlightArea, loadedMap, tmpZoomLevel, tmpMapCenter } = useRiverMap()
const unClusteredGeojson = ref(null)
const mapZoom = ref(9)
const mapCenter = ref([props.pointList[currentIndex.value].lon, props.pointList[currentIndex.value].lat])
const updateTime = ref('')

const onClickLink = (index) => {
  if (currentIndex.value === index) {
    return navigateTo(`/${route.params.rvrsys}/?pid=${props.pointList[index].id.replace(/^cctv_/, '')}`)
  } else {
    currentIndex.value = index
    const point = props.pointList[index]
    if (!point) return
    const { lat, lon } = point
    if (!loadedMap.value) return
    const map = loadedMap.value
    map.flyTo({
      center: [lon, lat],
      zoom: 9,
      speed: 1,
      essential: true
    })
  }
}

watch(currentIndex, (newIndex) => {
  const el = linkRefs.value[newIndex]
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }
})

watch(currentPointId, () => {
  if (!csvData.value || !waterLevelData.value) return
  const { clusteredGeoJson, unclusteredGeoJson } = makeGeoJson(csvData.value, waterLevelData.value, currentPointId.value)
  sourceGeojson.value = clusteredGeoJson
  unClusteredGeojson.value = unclusteredGeoJson
  updatePointIcon(currentPointId.value)
})

watch(() => clusterGeojson.value, (newValue) => {
  if (!loadedMap.value) return
  const map = loadedMap.value
  const source = map.getSource('obsCamClusteredPoints')
  source.setData(newValue)
})

watch(() => unClusteredGeojson.value, (newValue) => {
  if (!loadedMap.value) return
  const map = loadedMap.value
  const source = map.getSource('obsCamUnClusteredPoints')
  source.setData(newValue)
})

watch(() => tmpZoomLevel.value, (newValue) => {
  zoomLevel.value = newValue
  mapZoom.value = newValue
})

watch(() => tmpMapCenter.value, (newValue) => {
  mapCenter.value = newValue
})

watch(() => selectedPref.value, async (newValue) => {
  if (newValue === 'none') return []
  const data = await $fetch('/api/search/citycode', { query: { pref: newValue } })
  cityList.value = data
  selectedCitycode.value = 'none'
})

watch(() => selectedCitycode.value, async (newValue) => {
  setHighlightArea(newValue)
  const minIndex = props.pointList.findIndex((item) => item.citycode === newValue)
  if (minIndex === -1) {
    const latlon = await $fetch(`/api/search/coordinate?citycode=${newValue}`)
    if (!loadedMap.value) return
    const map = loadedMap.value
    map.flyTo({
      center: latlon,
      zoom: 9,
      speed: 1,
      essential: true
    })
  } else {
    currentIndex.value = minIndex
    const point = props.pointList[minIndex]
    if (!point) return
    const { lat, lon } = point
    if (!loadedMap.value) return
    const map = loadedMap.value
    map.flyTo({
      center: [lon, lat],
      zoom: 9,
      speed: 1,
      essential: true
    })
  }
})

onMounted(async () => {
  const [, riskData] = await Promise.all([
    initMap(mapContainer.value, mapZoom.value, mapCenter.value),
    getRiverRiskData()
  ])
  setRiverRisk(riskData)
  const [csv, waterLevel] = await Promise.all([
    $fetch('/api/river/csv'),
    $fetch('/api/river/waterlevel')
  ])
  csvData.value = csv
  waterLevelData.value = waterLevel.list
  const { clusteredGeoJson, unclusteredGeoJson } = makeGeoJson(csv, waterLevel.list, currentPointId.value)
  sourceGeojson.value = clusteredGeoJson
  unClusteredGeojson.value = unclusteredGeoJson
  updateTime.value = waterLevel.update_time
  updatePointIcon(currentPointId.value)
})
</script>

<template>
  <div class="content">
    <div class="box">
      <div class="control">
        <div class="select-pref">
          <select
            v-model="selectedPref"
            class="select-box"
          >
            <option
              v-for="prefecture in prefectureList"
              :key="prefecture.key"
              :value="prefecture.key"
            >
              {{ prefecture.name }}
            </option>
          </select>
        </div>
        <div class="select-city">
          <select
            v-model="selectedCitycode"
            class="select-box"
          >
            <option
              v-for="city in cityList"
              :key="city.citycode"
              :value="city.citycode"
            >
              {{ city.cityname }}
            </option>
          </select>
        </div>
      </div>
      <div
        ref="mapContainer"
        class="map-container"
      >
        <RiverLegend :is-showed="false" />
      </div>
    </div>
    <div class="block">
      <div
        v-for="(item, index) in props.pointList"
        :key="index"
        :ref="el => linkRefs[index] = el"
        :data-selected="currentIndex === index"
        class="link"
        @click="onClickLink(index)"
      >
        <p
          v-if="setRankText[item.rank]"
          class="rank"
          :data-rank="item.rank"
        >
          {{ setRankDetailText[item.rank] }}
        </p>
        <div class="text-box">
          <p class="info">
            {{ item.address }}
          </p>
          <p class="text">
            {{ item.name }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .box {
    display: flex;
    flex-direction: column;
    position: relative;
    background-color: #303030;
    max-width: 100%;
    .control {
      position: absolute;
      top: 8px;
      left: 6.5px;
      display: flex;
      gap: 10px;
      padding: 8px;
      background-color: #FFFFFF;
      border-radius: 8px;
      z-index: 2;
      .select-pref {
        position: relative;
        min-width: 80px;
        height: 24px;
        cursor: pointer;
        font-size: 12px;
        border: 1px solid #CCCCCC;
        &::before {
          content: '';
          position: absolute;
          top: 50%;
          right: 8px;
          transform: translateY(-50%);
          width: 12px;
          height: 8px;
          background-image: url('https://gvs.weathernews.jp/onebox/img/river/dropdown-arrow.svg');
          background-position: 50%;
          background-size: cover;
          background-repeat: no-repeat;
          pointer-events: none;
        }
        select {
          width: 100%;
          height: 100%;
          border: none;
          outline: none;
          padding: 0 30px 0 8px;
          font-size: 12px;
          cursor: pointer;
        }
      }
      .select-city {
        position: relative;
        min-width: 80px;
        height: 24px;
        cursor: pointer;
        font-size: 12px;
        border: 1px solid #CCCCCC;
        &::before {
          content: '';
          position: absolute;
          top: 50%;
          right: 8px;
          transform: translateY(-50%);
          width: 12px;
          height: 8px;
          background-image: url('https://gvs.weathernews.jp/onebox/img/river/dropdown-arrow.svg');
          background-position: 50%;
          background-size: cover;
          background-repeat: no-repeat;
          pointer-events: none;
        }
        select {
          width: 100%;
          height: 100%;
          border: none;
          outline: none;
          padding: 0 30px 0 8px;
          font-size: 12px;
          cursor: pointer;
        }
      }
    }
    .map-container {
      position: relative;
      overflow: hidden;
      aspect-ratio: 4 / 3;
    }
  }
  .block {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 5px;
    max-height: 183px;
    overflow-y: scroll;
    padding: 6px 10px;
    border: 1px solid #CCC;
    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-track {
      background: #EEF1F8;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #438FEC;
    }
    .link {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
      padding: 5px 14px 5px 6px;
      border-bottom: 1px solid #CCC;
      cursor: pointer;
      &[data-selected="true"] {
        background-color: #EEF1F8;
        background-image: url('https://gvs.weathernews.jp/onebox/img/common/icon_arrow_button_tall.svg');
        background-position: right 1px center;
        background-repeat: no-repeat;
        background-size: 13px;
        .text {
          color: #303030;
        }
      }
      .text-box{
        display: flex;
        flex-direction: column;
        width: 100%;
        .info {
          color: #303030;
          font-size: 12px;
          font-weight: 300;
          line-height: 12px;
          max-width: 100%;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .text {
          color: #3569c0;
          font-size: 14px;
          font-weight: 600;
          line-height: 22px;
        }
      }
      .rank {
        padding: 3px 5px;
        color: #FFF;
        font-size: 10px;
        font-weight: 600;
        line-height: 1em;
        border-radius: 2px;
        background-color: #FFF;
        max-width: 100%;
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        &[data-rank="5"] {
          background-color: #0C000C;
        }
        &[data-rank="4"] {
          background-color: #A0A;
        }
        &[data-rank="3"] {
          background-color: #FF2800;
        }
        &[data-rank="2"] {
          background-color: #F2E700;
          color: #303030;
        }
      }
    }
  }
}
@media (any-hover: hover) {
  .content {
    .block {
      .link {
        &:not([data-selected="true"]) {
          transition: opacity .2s ease-in-out;
          &:hover {
            opacity: .6;
          }
        }
      }
    }
  }
}
@media screen and (min-width: 830px) {
  .content {
    flex-direction: row;
    gap: 10px;
    min-height: 555px;
    .box {
      max-width: 64%;
      .control {
        .select-pref {
          select {
            font-size: 14px;
          }
        }
        .select-city {
          select {
            font-size: 14px;
          }
        }
      }
      .map-container {
        width: 100%;
        height: 555px;
      }
    }
    .block {
      max-height: 555px;
      padding: 10px;
      .link {
        .text-box {
          p {
            font-size: 15px;
          }
        }
      }
    }
  }
}
</style>
